// src/utils/bucketingState.ts

export type BucketType = 'high' | 'medium' | 'low' | 'done' | 'delete';

export interface BucketDecision {
  bucket: BucketType;
  willDoToday: boolean;
}

export interface BucketingState {
  phase: 'bucketing';
  allTasks: string[];
  currentIndex: number;
  decisions: Record<string, BucketDecision>;
  history: string[]; // Task IDs in order of decision for undo
}

export interface BucketSummary {
  today: number;
  high: number;
  medium: number;
  low: number;
  done: number;
  deleted: number;
}

export interface TournamentRecord {
  wins: number;
  losses: number;
  ties: number;
}

export interface Match {
  taskA: string;
  taskB: string;
  winner: string | null;
}

export interface MatchResult {
  match: Match;
  round: number;
  matchIndex: number;
}

export interface TournamentState {
  phase: 'tournament';
  tasks: string[];
  currentRound: number;
  totalRounds: number;
  matches: Match[];
  currentMatchIndex: number;
  results: Record<string, TournamentRecord>;
  eliminatedTasks: string[];
  history: MatchResult[];
}

export interface RerankState {
  bucketingState: BucketingState | null;
  tournamentState: TournamentState | null;
  bucketSummary: BucketSummary | null;
}

/**
 * Initialize bucketing state for a list of task IDs.
 */
export function initializeBucketing(taskIds: string[]): BucketingState {
  return {
    phase: 'bucketing',
    allTasks: [...taskIds],
    currentIndex: 0,
    decisions: {},
    history: [],
  };
}

/**
 * Record a bucket decision for the current task.
 */
export function recordBucketDecision(
  state: BucketingState,
  bucket: BucketType,
  willDoToday: boolean
): BucketingState {
  const taskId = state.allTasks[state.currentIndex];
  if (!taskId) return state;

  return {
    ...state,
    currentIndex: state.currentIndex + 1,
    decisions: {
      ...state.decisions,
      [taskId]: { bucket, willDoToday },
    },
    history: [...state.history, taskId],
  };
}

/**
 * Undo the last bucket decision.
 */
export function undoBucketDecision(state: BucketingState): BucketingState {
  if (state.history.length === 0) return state;

  const lastTaskId = state.history[state.history.length - 1];
  const newDecisions = { ...state.decisions };
  delete newDecisions[lastTaskId];

  return {
    ...state,
    currentIndex: state.currentIndex - 1,
    decisions: newDecisions,
    history: state.history.slice(0, -1),
  };
}

/**
 * Check if bucketing is complete.
 */
export function isBucketingComplete(state: BucketingState): boolean {
  return state.currentIndex >= state.allTasks.length;
}

/**
 * Get bucket summary from completed bucketing.
 */
export function getBucketSummary(state: BucketingState): BucketSummary {
  let today = 0;
  let high = 0;
  let medium = 0;
  let low = 0;
  let done = 0;
  let deleted = 0;

  for (const decision of Object.values(state.decisions)) {
    if (decision.willDoToday) {
      today++;
    }
    switch (decision.bucket) {
      case 'high':
        high++;
        break;
      case 'medium':
        medium++;
        break;
      case 'low':
        low++;
        break;
      case 'done':
        done++;
        break;
      case 'delete':
        deleted++;
        break;
    }
  }

  return { today, high, medium, low, done, deleted };
}

/**
 * Get task IDs in the high bucket (for tournament).
 */
export function getHighBucketTasks(state: BucketingState): string[] {
  return Object.entries(state.decisions)
    .filter(([, decision]) => decision.bucket === 'high')
    .map(([taskId]) => taskId);
}

/**
 * Get task IDs marked as done during bucketing.
 */
export function getDoneTasks(state: BucketingState): string[] {
  return Object.entries(state.decisions)
    .filter(([, decision]) => decision.bucket === 'done')
    .map(([taskId]) => taskId);
}

/**
 * Get task IDs marked for deletion during bucketing.
 */
export function getDeletedTasks(state: BucketingState): string[] {
  return Object.entries(state.decisions)
    .filter(([, decision]) => decision.bucket === 'delete')
    .map(([taskId]) => taskId);
}

/**
 * Serialize state for Firestore storage.
 */
export function serializeRerankState(state: RerankState): string {
  return JSON.stringify(state);
}

/**
 * Deserialize state from Firestore storage.
 */
export function deserializeRerankState(json: string): RerankState | null {
  try {
    return JSON.parse(json) as RerankState;
  } catch {
    return null;
  }
}
